import type { NodeId, QueryView } from "~/models/dto";
import { createSlug } from "~/tools/create-slug";
import type { ImmutableMap } from "~/tools/immutable-map";

import { selectComponentIdentifier, selectViewSlug } from "../../../app/state/route/RouteSelectors";
import type { AllSearchParams } from "../../../app/state/route/SearchParams";
import {
	DEFAULT_COMPONENT_VIEW_POINT,
	type ViewShareableState,
	diffViewShareableState,
	generateExploreSubviewQuery,
	generateViewComponentQuery,
	mergeViewShareableState,
} from "../../../app/state/view/viewpoint/ViewPointQueryUtils";

import type { NavigationMiddleware } from "./NavigationMiddleware";

const findCleanCurrentView = (
	pathname: string,
	views: ImmutableMap<NodeId, QueryView>,
): undefined | ViewShareableState => {
	const componentIdentifier = selectComponentIdentifier.resultFunc(pathname);
	if (componentIdentifier != null) {
		return {
			...DEFAULT_COMPONENT_VIEW_POINT,
			query: generateViewComponentQuery(componentIdentifier),
		};
	}

	const viewSlugOrIdentifier = selectViewSlug.resultFunc(pathname);
	if (viewSlugOrIdentifier == null) {
		return undefined;
	}

	return views.find(
		({ identifier, name }) => identifier === viewSlugOrIdentifier || createSlug(name) === viewSlugOrIdentifier,
	);
};

const patchCleanViewByExplore = (
	search: AllSearchParams,
	view: undefined | ViewShareableState,
): undefined | Partial<ViewShareableState> => {
	if (view == null || search.explore == null) {
		return view;
	}

	return mergeViewShareableState(view, {
		...search.originView,
		// don't apply the query if it is exactly the same as the subview query
		query: generateExploreSubviewQuery(search.explore),
	});
};

/**
 * A middleware that applies the diff between the current view and the view in the search params.
 * It makes sure that the resulting query has only the fields that are different from the saved view.
 *
 * @param views - a map of views
 * @returns a middleware function
 */
export const applyViewDiffMiddleware =
	(views: ImmutableMap<NodeId, QueryView>): NavigationMiddleware =>
	({ pathname, search }) => {
		const cleanView = patchCleanViewByExplore(search, findCleanCurrentView(pathname, views));

		if (cleanView == null) {
			return { pathname, search };
		}

		const { query, ...view } = diffViewShareableState(cleanView, { ...search.view, query: search.query }) ?? {};

		return {
			pathname,
			search: { ...search, query, view },
		};
	};
